import type { ProjectScript } from "@cmmd-forge/contracts";

export { buildPortAwareDevServerCommand } from "./devServerCommandArgs";

/**
 * Terminal id shared by the human "Launch app" button (BrowserPreviewPanel)
 * and the `forge_dev_server_start` agent tool, so both surfaces write dev
 * server output into the same terminal pane the user already has open.
 */
export const BROWSER_PREVIEW_TERMINAL_ID = "browser-preview";

const DEV_SERVER_HINT_PATTERN =
  /\b(dev|start|serve|preview|watch|vite|next dev|nuxt dev|astro dev|webpack serve|react-scripts start|svelte-kit|remix)\b/i;
const NON_DEV_SCRIPT_PATTERN = /\b(test|lint|build|typecheck|format|fmt|coverage|e2e|spec)\b/i;
const HEURISTIC_DEV_COMMAND =
  'if [ -f "package.json" ]; then (bun run dev || npm run dev || pnpm dev || yarn dev); elif [ -f "deno.json" ] || [ -f "deno.jsonc" ]; then deno task dev; else echo "No dev command detected for this project." >&2; fi';

export interface DevServerLaunchPlan {
  readonly id: string;
  readonly name: string;
  readonly command: string;
  readonly source: "project-script" | "heuristic";
}

export function resolveDevServerLaunchPlan(
  projectScripts: readonly ProjectScript[],
): DevServerLaunchPlan {
  const matchedScript = pickDevServerScript(projectScripts);
  if (matchedScript) {
    return {
      id: matchedScript.id,
      name: matchedScript.name,
      command: matchedScript.command,
      source: "project-script",
    };
  }

  return {
    id: "auto-detect-dev",
    name: "Auto-detect dev command",
    command: HEURISTIC_DEV_COMMAND,
    source: "heuristic",
  };
}

export function scoreDevServerScript(script: ProjectScript): number {
  if (script.runOnWorktreeCreate) {
    return -1;
  }
  const name = script.name.toLowerCase();
  const command = script.command.toLowerCase();
  const combined = `${name} ${command}`;

  if (NON_DEV_SCRIPT_PATTERN.test(combined) && !DEV_SERVER_HINT_PATTERN.test(combined)) {
    return -1;
  }

  let score = 0;
  if (name === "dev") score += 60;
  if (name === "start") score += 50;
  if (/\b(dev|start|serve|preview)\b/.test(name)) score += 30;
  if (DEV_SERVER_HINT_PATTERN.test(combined)) score += 25;
  if (/\b(vite|webpack|next|nuxt|astro|svelte-kit|remix)\b/.test(command)) score += 15;
  if (/\b(--watch|watch)\b/.test(command)) score += 5;

  return score;
}

export function pickDevServerScript(scripts: readonly ProjectScript[]): ProjectScript | null {
  let bestScript: ProjectScript | null = null;
  let bestScore = 0;
  for (const script of scripts) {
    const score = scoreDevServerScript(script);
    if (score <= bestScore) {
      continue;
    }
    bestScript = script;
    bestScore = score;
  }
  return bestScript;
}
